import { CommunitySubSector, DisclosurePageKey, SourceType } from '@/types';
import { communitySubSectors, sourceTypes } from '@/data/labels';

// The single front-end home for the disclosure page-key scheme.
//
// A page key is a server contract — village's `PageKey`, encoded as
// `overview` | `source:<source-type-kebab>` — carried in the `?page=` query
// param and the `/summary` page descriptors. The encoding (the `source:`
// prefix) lives here and nowhere else: build a key with `sourcePageKey`, read
// one with `parsePageKey`. Interpolating `source:${…}` at call sites scatters
// the contract and drifts from the server; routing it through this codec keeps
// it in one place (mirrors suit's `data/pages`).

const SOURCE_PREFIX = 'source:';
const SECTOR_PREFIX = 'sector:';

// The page key for a source's page. The one place the `source:` prefix is
// written; `isPageKey` validates against it too, so the encoding has a single
// definition.
const sourcePageKey = (sourceType: SourceType): DisclosurePageKey =>
  `${SOURCE_PREFIX}${sourceType}`;

// The page key for a sub-sector's page — the by-sector counterpart, the one
// place the `sector:` prefix is written. (`sectors`, the breakdown landing
// page, is a fixed top-level key, like `overview`.)
const sectorPageKey = (subSector: CommunitySubSector): DisclosurePageKey =>
  `${SECTOR_PREFIX}${subSector}`;

// A raw string is a valid page key iff it is a fixed key (`overview`,
// `location`, `methodology`, `sectors`, the two GPC pages), the source-page
// key of a known source type, or the sector-page key of a known community
// sub-sector. Guards the type, not presence — a well-formed key for an absent
// page is still "valid" (its page query returns `not-found`).
const isPageKey = (raw: string): raw is DisclosurePageKey =>
  raw === 'overview' ||
  raw === 'location' ||
  raw === 'methodology' ||
  raw === 'sectors' ||
  raw === 'gpc-summary' ||
  raw === 'gpc-detail' ||
  sourceTypes.some((source) => raw === sourcePageKey(source)) ||
  communitySubSectors.some((subSector) => raw === sectorPageKey(subSector));

// Parse a raw `?page=` value (possibly null/garbage from the URL) to a page
// key, or `null` if it is absent or not a well-formed key. No default is
// baked in: the landing page is the server's `firstPage` (a disclosure need
// not have an Overview), so the caller supplies the fallback (mirrors suit's
// `parsePageKey` returning `PageKey | null`).
const parsePageKey = (raw: string | null): DisclosurePageKey | null =>
  raw !== null && isPageKey(raw) ? raw : null;

export { sourcePageKey, sectorPageKey, isPageKey, parsePageKey };
